// Environment configuration for HDOT Airport Checkpoint Management Application
// ALL CONFIGURATION COMES FROM .env ENVIRONMENT VARIABLES ONLY

export interface AppConfig {
  apiBaseUrl: string;
  displayApiBaseUrl: string;
  environment: 'dev' | 'staging' | 'prod';
  useMockData: boolean;
  appName: string;
  version: string;
  // Authentication configuration
  auth: {
    enableAuthentication: boolean;
    checkBackendAuthStatus: boolean;
    userGroupId?: string;
    // Azure Entra ID configuration
    azureClientId: string;
    azureTenantId: string;
    apiScope: string;
  };
  features: {
    sync: boolean;
    threeDView: boolean;
    realTimeUpdates: boolean;
  };
}

/**
 * Get configuration from environment variables only
 * No hardcoded configs - everything comes from .env file
 */
export const getConfig = (): AppConfig => {
  const environment = (process.env.REACT_APP_ENVIRONMENT as 'dev' | 'staging' | 'prod') || 'dev';
  
  return {
    // API Configuration - from .env
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
    displayApiBaseUrl: process.env.REACT_APP_DISPLAY_API_BASE_URL || 'http://localhost:8000',
    
    // Environment
    environment: environment,
    
    // Data Configuration - from .env
    useMockData: process.env.REACT_APP_USE_MOCK_DATA === 'true',
    
    // App Metadata
    appName: `HDOT Checkpoint Management Application${environment !== 'prod' ? ` (${environment.charAt(0).toUpperCase() + environment.slice(1)})` : ''}`,
    version: '1.0.0',
    
    // Authentication Configuration - from .env
    auth: {
      enableAuthentication: process.env.REACT_APP_ENABLE_AUTHENTICATION === 'true',
      checkBackendAuthStatus: process.env.REACT_APP_CHECK_BACKEND_AUTH_STATUS === 'true',
      userGroupId: process.env.REACT_APP_USER_GROUP_ID, // Optional override
      // Azure Entra ID configuration - from .env
      azureClientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
      azureTenantId: process.env.REACT_APP_AZURE_TENANT_ID || '',
      apiScope: process.env.REACT_APP_API_SCOPE || 'api://hdot-checkpoint-api/access_as_user',
    },
    
    // Feature Flags
    features: {
      sync: true,
      threeDView: environment !== 'dev', // Only enable in staging/prod
      realTimeUpdates: true
    }
  };
};

// Export the current configuration getter
export const getCurrentConfig = getConfig;

// Export config as a getter function to avoid caching
export const config = getConfig();

// Helper functions - always get fresh config from environment variables
export const isDev = () => (process.env.REACT_APP_ENVIRONMENT || 'dev') === 'dev';
export const isStaging = () => process.env.REACT_APP_ENVIRONMENT === 'staging';
export const isProd = () => process.env.REACT_APP_ENVIRONMENT === 'prod';
export const isMockMode = () => process.env.REACT_APP_USE_MOCK_DATA === 'true';

// API Helper functions - directly from environment variables
export const getApiBaseUrl = () => process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
export const getDisplayApiBaseUrl = () => process.env.REACT_APP_DISPLAY_API_BASE_URL || 'http://localhost:8000';

// Auth helper functions - directly from environment variables
export const isAuthenticationEnabled = () => process.env.REACT_APP_ENABLE_AUTHENTICATION === 'true';
export const shouldCheckBackendAuthStatus = () => process.env.REACT_APP_CHECK_BACKEND_AUTH_STATUS === 'true';
export const getAuthUserGroupId = () => process.env.REACT_APP_USER_GROUP_ID;

// Azure Entra ID helper functions - directly from environment variables
export const getAzureClientId = () => process.env.REACT_APP_AZURE_CLIENT_ID || '';
export const getAzureTenantId = () => process.env.REACT_APP_AZURE_TENANT_ID || '';
export const getApiScope = () => process.env.REACT_APP_API_SCOPE || 'api://hdot-checkpoint-api/access_as_user';

// Debug helper functions - directly from environment variables
export const isDebugMode = () => process.env.REACT_APP_DEBUG_MODE === 'true';
export const isConsoleLoggingEnabled = () => process.env.REACT_APP_CONSOLE_LOGGING === 'true';

// Feature flags - always get fresh config
export const features = getConfig().features;

// Development helper functions
export const isDevelopment = () => process.env.NODE_ENV === 'development';
export const isProduction = () => process.env.NODE_ENV === 'production';

// Environment info for debugging (conditional on debug mode)
if (process.env.REACT_APP_DEBUG_MODE === 'true') {
  // Config Debug info available
}
